/* ============================================================================
   PARHELIK — Product compare (framework-free)

   Ports minimog's compare-product.js behaviour onto the Parhelik compare page
   (sections/page-product-compare.liquid).

   Responsibilities
     1. Manage a localStorage compare list of product handles (de-duplicated,
        capped at the page's max-columns). Persisted under "parhelik-compare".
     2. Wire every [data-ph-compare-add] control across the store (product cards,
        PDP, quick-view) as a TOGGLE: add/remove the product's handle, reflect the
        added/removed state on the control (`.is-active` + aria-pressed + label
        swap via [data-revert-label]), and keep all controls for the same handle
        in sync. Update any [data-ph-compare-count] badges.
     3. On the compare page ([data-ph-compare-page]), fetch each stored product's
        JSON (/products/{handle}.js), then build the Parhelik comparison TABLE —
        attribute rows x product columns — with per-column remove buttons and an
        empty state. Re-renders when the list changes (remove / clear / cross-tab
        storage events).

   Re-inits on shopify:section:load (theme editor). No dependencies. Guarded so
   double-loading the asset is harmless.
   ========================================================================== */
(function () {
  'use strict';

  if (window.PHCompare && window.PHCompare._installed) return;

  var STORAGE_KEY = 'parhelik-compare';
  var DEFAULT_MAX = 4;
  var ADD_SELECTOR = '[data-ph-compare-add]';
  var PAGE_SELECTOR = '[data-ph-compare-page]';
  var ADDED_CLASS = 'is-active';

  /* ---------------------------- localStorage ---------------------------- */
  function readList() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      // strings only, de-duplicated, drop blanks
      var seen = {};
      var out = [];
      parsed.forEach(function (v) {
        if (typeof v === 'string' && v && !seen[v]) {
          seen[v] = 1;
          out.push(v);
        }
      });
      return out;
    } catch (e) {
      return [];
    }
  }

  function writeList(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage unavailable / quota — fail silently */
    }
  }

  function maxColumns() {
    var page = document.querySelector(PAGE_SELECTOR);
    var n = page ? parseInt(page.getAttribute('data-max-columns'), 10) : NaN;
    return isNaN(n) || n < 1 ? DEFAULT_MAX : n;
  }

  // Returns { added: Boolean } so callers can reflect UI.
  function toggle(handle) {
    if (!handle) return { added: false };
    var list = readList();
    var idx = list.indexOf(handle);
    if (idx >= 0) {
      list.splice(idx, 1);
      writeList(list);
      onChange(handle, false);
      return { added: false };
    }
    if (list.length >= maxColumns()) {
      // At capacity — drop the oldest to make room (FIFO), keep it usable.
      var removed = list.shift();
      if (removed) syncButtons(removed, false);
    }
    list.push(handle);
    writeList(list);
    onChange(handle, true);
    return { added: true };
  }

  function remove(handle) {
    if (!handle) return;
    var list = readList();
    var idx = list.indexOf(handle);
    if (idx < 0) return;
    list.splice(idx, 1);
    writeList(list);
    onChange(handle, false);
  }

  function clear() {
    var list = readList();
    writeList([]);
    list.forEach(function (h) { syncButtons(h, false); });
    updateCounts();
    renderPages();
  }

  // Central reaction to a single-handle change: sync buttons, counts, page.
  function onChange(handle, added) {
    syncButtons(handle, added);
    updateCounts();
    renderPages();
  }

  /* ------------------------- toggle controls --------------------------- */
  function handleOf(btn) {
    if (!btn) return '';
    return (
      btn.getAttribute('data-ph-compare-add') ||
      btn.getAttribute('data-product-handle') ||
      ''
    );
  }

  // Reflect added/removed state on every control bound to `handle`.
  function syncButtons(handle, added) {
    if (!handle) return;
    var btns = document.querySelectorAll(ADD_SELECTOR);
    for (var i = 0; i < btns.length; i++) {
      if (handleOf(btns[i]) !== handle) continue;
      setButtonState(btns[i], added);
    }
  }

  function setButtonState(btn, added) {
    if (!btn) return;
    if (added) {
      btn.classList.add(ADDED_CLASS);
      btn.setAttribute('aria-pressed', 'true');
    } else {
      btn.classList.remove(ADDED_CLASS);
      btn.setAttribute('aria-pressed', 'false');
    }
    // Optional label swap: <span data-ph-compare-label data-revert-label="Compared">
    var label = btn.querySelector('[data-ph-compare-label]');
    if (label && label.getAttribute('data-revert-label') != null) {
      var current = label.textContent;
      label.textContent = label.getAttribute('data-revert-label');
      label.setAttribute('data-revert-label', current);
    }
  }

  // On first paint, mark controls whose handle is already in the list.
  function primeButtons(scope) {
    var list = readList();
    var btns = (scope || document).querySelectorAll(ADD_SELECTOR);
    for (var i = 0; i < btns.length; i++) {
      var inList = list.indexOf(handleOf(btns[i])) >= 0;
      if (inList && !btns[i].classList.contains(ADDED_CLASS)) {
        setButtonState(btns[i], true);
      } else {
        btns[i].setAttribute('aria-pressed', inList ? 'true' : 'false');
      }
    }
  }

  function updateCounts() {
    var size = readList().length;
    var els = document.querySelectorAll('[data-ph-compare-count]');
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = String(size);
    }
  }

  /* --------------------------- click handling -------------------------- */
  function onDocumentClick(e) {
    var btn = e.target.closest ? e.target.closest(ADD_SELECTOR) : null;
    if (!btn) return;
    e.preventDefault();
    var handle = handleOf(btn);
    if (!handle) return;
    toggle(handle);
  }

  /* ------------------------------ helpers ------------------------------ */
  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Minimal money formatter mirroring Shopify's money_format tokens. Falls back
  // to a plain "$X.XX" when the format string is missing.
  function formatMoney(cents, format) {
    if (cents == null) return '';
    var value = (parseInt(cents, 10) || 0) / 100;
    if (!format) {
      return '$' + value.toFixed(2);
    }
    function withCommas(num, decimals) {
      var fixed = (parseFloat(num) || 0).toFixed(decimals);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, token) {
      switch (token) {
        case 'amount':
          return withCommas(value, 2);
        case 'amount_no_decimals':
          return withCommas(value, 0);
        case 'amount_with_comma_separator':
          return withCommas(value, 2).replace(/,/g, '~').replace(/\./g, ',').replace(/~/g, '.');
        case 'amount_no_decimals_with_comma_separator':
          return withCommas(value, 0).replace(/,/g, '~').replace(/\./g, ',').replace(/~/g, '.');
        case 'amount_with_space_separator':
          return withCommas(value, 2).replace(/,/g, ' ');
        case 'amount_no_decimals_with_space_separator':
          return withCommas(value, 0).replace(/,/g, ' ');
        default:
          return '';
      }
    });
  }

  function resizeImage(src, width) {
    if (!src) return '';
    // Shopify CDN urls accept a width param; safe no-op otherwise.
    try {
      var u = new URL(src, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return src;
    }
  }

  function readLabels(page) {
    var node = page.querySelector('[data-ph-compare-labels]');
    var fallback = {
      image: 'Product', title: 'Name', price: 'Price', vendor: 'Brand',
      type: 'Type', availability: 'Availability', description: 'Description',
      in_stock: 'In stock', sold_out: 'Sold out', from: 'From',
      remove: 'Remove', view: 'View product'
    };
    if (!node) return fallback;
    try {
      var parsed = JSON.parse(node.textContent);
      for (var k in fallback) {
        if (!parsed[k]) parsed[k] = fallback[k];
      }
      return parsed;
    } catch (e) {
      return fallback;
    }
  }

  /* ------------------------------ fetch -------------------------------- */
  var productCache = {};

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('compare http ' + r.status);
        return r.json();
      })
      .then(function (data) {
        productCache[handle] = data;
        return data;
      })
      .catch(function () {
        return null;
      });
  }

  /* ------------------------- table cell builders ----------------------- */
  function removeSvg() {
    return (
      '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '</svg>'
    );
  }

  function imageCell(product, labels) {
    var img = product.featured_image || (product.media && product.media[0] && product.media[0].preview_image && product.media[0].preview_image.src);
    var media;
    if (img) {
      var src = resizeImage(img.indexOf('//') === 0 ? 'https:' + img : img, 600);
      media =
        '<img class="ph-compare__img" src="' + escapeHtml(src) + '" alt="' +
        escapeHtml(product.title) + '" loading="lazy" width="600" height="600">';
    } else {
      media =
        '<svg class="ph-compare__img--placeholder" viewBox="0 0 100 100" aria-hidden="true">' +
        '<rect width="100" height="100" fill="currentColor"/></svg>';
    }
    return (
      '<a class="ph-compare__media" href="' + escapeHtml(product.url || ('/products/' + product.handle)) +
      '" aria-label="' + escapeHtml(product.title) + '">' +
      media +
      '<button type="button" class="ph-compare__remove" data-ph-compare-remove="' +
      escapeHtml(product.handle) + '" aria-label="' + escapeHtml(labels.remove) +
      ' — ' + escapeHtml(product.title) + '" title="' + escapeHtml(labels.remove) + '">' +
      removeSvg() +
      '</button>' +
      '</a>'
    );
  }

  function titleCell(product) {
    return (
      '<a class="ph-compare__name" href="' +
      escapeHtml(product.url || ('/products/' + product.handle)) + '">' +
      escapeHtml(product.title) + '</a>'
    );
  }

  function priceCell(product, page, labels) {
    var format = page.getAttribute('data-money-format') || '';
    var price = product.price;
    var compareAt = product.compare_at_price;
    var varies = product.price_varies;
    var onSale = compareAt && compareAt > price;
    var html = '';
    if (varies) {
      html += '<span class="ph-compare__price-from">' + escapeHtml(labels.from) + '</span>';
    }
    html +=
      '<span class="ph-compare__price' + (onSale ? ' ph-compare__price--sale' : '') + '">' +
      escapeHtml(formatMoney(price, format)) + '</span>';
    if (onSale) {
      html += '<s class="ph-compare__compare-at">' + escapeHtml(formatMoney(compareAt, format)) + '</s>';
    }
    return html;
  }

  function textCell(value) {
    if (!value) return '<span class="ph-compare__value ph-compare__value--muted">&mdash;</span>';
    return '<span class="ph-compare__value">' + escapeHtml(value) + '</span>';
  }

  function availabilityCell(product, labels) {
    var available = !!product.available;
    return (
      '<span class="ph-compare__stock ph-compare__stock--' + (available ? 'in' : 'out') + '">' +
      escapeHtml(available ? labels.in_stock : labels.sold_out) +
      '</span>'
    );
  }

  function optionsCell(product) {
    var opts = product.options || [];
    // /products.js returns options as array of names, plus variants carry values.
    // Build name -> distinct values map from variants when present.
    var names = opts.map(function (o) {
      return typeof o === 'string' ? o : (o && o.name) || '';
    });
    if (!names.length || (names.length === 1 && names[0].toLowerCase() === 'title')) {
      return '<span class="ph-compare__value ph-compare__value--muted">&mdash;</span>';
    }
    var valuesByPos = [[], [], []];
    var seen = [{}, {}, {}];
    (product.variants || []).forEach(function (v) {
      for (var p = 1; p <= 3; p++) {
        var val = v['option' + p];
        if (val && !seen[p - 1][val]) {
          seen[p - 1][val] = 1;
          valuesByPos[p - 1].push(val);
        }
      }
    });
    var items = '';
    for (var i = 0; i < names.length && i < 3; i++) {
      if (!names[i]) continue;
      var vals = valuesByPos[i];
      var valText = vals.length ? vals.join(', ') : '';
      items +=
        '<li class="ph-compare__spec">' +
        '<span class="ph-compare__spec-name">' + escapeHtml(names[i]) + '</span>' +
        escapeHtml(valText) +
        '</li>';
    }
    if (!items) return '<span class="ph-compare__value ph-compare__value--muted">&mdash;</span>';
    return '<ul class="ph-compare__specs">' + items + '</ul>';
  }

  function descriptionCell(product) {
    var text = (product.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (text.length > 200) text = text.slice(0, 200).replace(/\s+\S*$/, '') + '…';
    return textCell(text);
  }

  function actionCell(product, page, labels) {
    var isDark = page.className.indexOf('ph-section--dark') >= 0;
    var btnClass = isDark ? 'ph-btn ph-btn--invert' : 'ph-btn ph-btn--ghost';
    return (
      '<div class="ph-compare__action"><a class="' + btnClass + '" href="' +
      escapeHtml(product.url || ('/products/' + product.handle)) + '">' +
      escapeHtml(labels.view) + '</a></div>'
    );
  }

  /* ----------------------------- rendering ----------------------------- */
  // Build one <tr> for a row key across all products.
  function buildRow(key, label, products, page, labels) {
    var cells = products
      .map(function (p) {
        var content;
        switch (key) {
          case 'image': content = imageCell(p, labels); break;
          case 'title': content = titleCell(p); break;
          case 'price': content = priceCell(p, page, labels); break;
          case 'vendor': content = textCell(p.vendor); break;
          case 'type': content = textCell(p.type); break;
          case 'availability': content = availabilityCell(p, labels); break;
          case 'options': content = optionsCell(p); break;
          case 'description': content = descriptionCell(p); break;
          case 'action': content = actionCell(p, page, labels); break;
          default: content = '';
        }
        return '<td class="ph-compare__col">' + content + '</td>';
      })
      .join('');
    var labelCell = '<th class="ph-compare__rowlabel" scope="row">' + escapeHtml(label) + '</th>';
    return '<tr data-row="' + key + '">' + labelCell + cells + '</tr>';
  }

  function renderTable(page, products) {
    var table = page.querySelector('[data-ph-compare-table]');
    if (!table) return;
    var labels = readLabels(page);

    // Always-on rows + merchant-selected rows from data-rows.
    var requested = (page.getAttribute('data-rows') || '').split(',').filter(Boolean);
    var rowOrder = ['image', 'title'];
    ['price', 'vendor', 'type', 'availability', 'options', 'description'].forEach(function (k) {
      if (requested.indexOf(k) >= 0) rowOrder.push(k);
    });
    rowOrder.push('action');

    var rowsHtml = '';
    for (var i = 0; i < rowOrder.length; i++) {
      var key = rowOrder[i];
      // image/title/action rows have no visible label text per the design;
      // still give the row label cell content for the spec rows.
      var labelText = key === 'image' ? labels.image
        : key === 'title' ? labels.title
        : key === 'action' ? ''
        : labels[key] || key;
      rowsHtml += buildRow(key, labelText, products, page, labels);
    }
    table.innerHTML = rowsHtml;
  }

  function showState(page, state) {
    var loading = page.querySelector('[data-ph-compare-loading]');
    var scroll = page.querySelector('[data-ph-compare-scroll]');
    var empty = page.querySelector('[data-ph-compare-empty]');
    var meta = page.querySelector('[data-ph-compare-meta]');
    if (loading) loading.hidden = state !== 'loading';
    if (scroll) scroll.hidden = state !== 'table';
    if (empty) empty.hidden = state !== 'empty';
    if (meta) meta.hidden = state === 'loading';
  }

  function renderPage(page) {
    if (!page) return;
    var list = readList();
    updateCounts();

    if (!list.length) {
      var emptyTable = page.querySelector('[data-ph-compare-table]');
      if (emptyTable) emptyTable.innerHTML = '';
      showState(page, 'empty');
      return;
    }

    showState(page, 'loading');

    Promise.all(list.map(fetchProduct)).then(function (results) {
      // Preserve list order; drop products that failed / no longer exist and
      // prune them from storage so the list self-heals.
      var products = [];
      var validHandles = [];
      list.forEach(function (handle, i) {
        var data = results[i];
        if (data && data.id) {
          products.push(data);
          validHandles.push(handle);
        }
      });

      if (validHandles.length !== list.length) {
        writeList(validHandles);
        updateCounts();
      }

      if (!products.length) {
        showState(page, 'empty');
        return;
      }

      renderTable(page, products);
      showState(page, 'table');
    });
  }

  function renderPages() {
    var pages = document.querySelectorAll(PAGE_SELECTOR);
    for (var i = 0; i < pages.length; i++) {
      renderPage(pages[i]);
    }
  }

  /* ----------------------- page-local remove clicks -------------------- */
  function onPageClick(e) {
    var page = e.target.closest ? e.target.closest(PAGE_SELECTOR) : null;
    if (!page) return;

    var removeBtn = e.target.closest('[data-ph-compare-remove]');
    if (removeBtn) {
      e.preventDefault();
      remove(removeBtn.getAttribute('data-ph-compare-remove'));
      return;
    }

    var clearBtn = e.target.closest('[data-ph-compare-clear]');
    if (clearBtn) {
      e.preventDefault();
      clear();
    }
  }

  /* ------------------------------- boot -------------------------------- */
  function boot(scope) {
    primeButtons(scope || document);
    updateCounts();
    renderPages();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  // Global delegated handlers (bound once).
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('click', onPageClick);

  // Cross-tab sync: another tab changed the list — reflect it here.
  window.addEventListener('storage', function (e) {
    if (e.key !== STORAGE_KEY) return;
    primeButtons(document);
    updateCounts();
    renderPages();
  });

  // Theme editor / re-render: re-prime injected controls + re-render the page.
  document.addEventListener('shopify:section:load', function (e) {
    var scope = (e && e.target && e.target.querySelectorAll) ? e.target : document;
    primeButtons(scope);
    updateCounts();
    renderPages();
  });

  /* ------------------------------- API --------------------------------- */
  window.PHCompare = {
    _installed: true,
    list: readList,
    toggle: toggle,
    add: function (handle) {
      var list = readList();
      if (list.indexOf(handle) < 0) toggle(handle);
      return readList();
    },
    remove: remove,
    clear: clear,
    refresh: function () { boot(document); }
  };
})();
